/**
 * Apply accessibility preferences by toggling data-attributes on <html>.
 * Each helper handles ONE concern so the surface is easy to read and test.
 * The data-attributes are consumed by /styles/accessibility.css.
 */

const setAttr = (html, name, value) => {
  if (value === false || value === "default" || value === "0" || value == null) {
    html.removeAttribute(name);
  } else {
    html.setAttribute(name, String(value));
  }
};

// ---------- Per-feature appliers ----------
const applyTypography = (html, s) => {
  setAttr(html, "data-a11y-text", s.textSize === "1" ? null : s.textSize);
  setAttr(html, "data-a11y-line-height", s.lineHeight === "0" ? null : s.lineHeight);
  setAttr(html, "data-a11y-letter-spacing", s.letterSpacing === "0" ? null : s.letterSpacing);
  setAttr(html, "data-a11y-readable-font", s.readableFont ? "true" : null);
  setAttr(html, "data-a11y-align", s.align === "default" ? null : s.align);
};

const applyColor = (html, s) => {
  setAttr(html, "data-a11y-contrast", s.contrast === "default" ? null : s.contrast);
  setAttr(html, "data-a11y-saturation", s.saturation === "default" ? null : s.saturation);
};

const applyHighlights = (html, s) => {
  setAttr(html, "data-a11y-highlight-links", s.highlightLinks ? "true" : null);
  setAttr(html, "data-a11y-highlight-titles", s.highlightTitles ? "true" : null);
  setAttr(html, "data-a11y-highlight-hover", s.highlightHover ? "true" : null);
};

const applyOrientation = (html, s) => {
  setAttr(html, "data-a11y-cursor", s.cursor === "default" ? null : s.cursor);
  setAttr(html, "data-a11y-stop-animations", s.stopAnimations ? "true" : null);
  setAttr(html, "data-a11y-hide-images", s.hideImages ? "true" : null);
  setAttr(html, "data-a11y-keyboard-nav", s.keyboardNav ? "true" : null);
};

const applyMediaState = (s) => {
  // Pause autoplaying videos when stop-animations is on; re-play if it was off.
  document.querySelectorAll("video").forEach((v) => {
    if (s.stopAnimations) {
      try {
        v.pause();
      } catch (e) {
        void e; // calling .pause() before metadata loads can throw; safe to ignore
      }
    } else if (v.autoplay) {
      try {
        v.play().catch(() => {
          /* autoplay blocked - page's own click-to-play handler takes over */
        });
      } catch (e) {
        void e;
      }
    }
  });
};

/**
 * Apply all accessibility settings to the DOM.
 * Cyclomatic complexity is intentionally split across small helpers.
 */
export const applySettings = (settings) => {
  const html = document.documentElement;
  applyTypography(html, settings);
  applyColor(html, settings);
  applyHighlights(html, settings);
  applyOrientation(html, settings);
  applyMediaState(settings);
};

export const STORAGE_KEY = "cml-a11y-settings";

export const DEFAULT_SETTINGS = {
  textSize: "1",
  lineHeight: "0",
  letterSpacing: "0",
  readableFont: false,
  align: "default",
  contrast: "default",
  saturation: "default",
  highlightLinks: false,
  highlightTitles: false,
  cursor: "default",
  stopAnimations: false,
  hideImages: false,
  keyboardNav: false,
  highlightHover: false,
  readingMask: false,
  readingGuide: false,
};

export const loadStoredSettings = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (e) {
    console.warn("a11y: failed to read settings from localStorage", e);
    return DEFAULT_SETTINGS;
  }
};

export const persistSettings = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn("a11y: failed to persist settings to localStorage", e);
  }
};
